import { computed, inject, Injectable, ResourceRef, Signal, signal } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { debounceTime, forkJoin, map, Observable, of, tap } from "rxjs";
import { HttpDataService } from "src/services/httpdata.service";
import { environment } from "src/environments/environment";
import {
    INewDwellingInfo,
    INewDwellingProjectInfo,
    INewDwellingProspectUnits,
    INewDwellingProjectStageImages,
    INeighborhoodModel,
    INewDwellingProjectData,
    INewDwellingDocumentInfo,
    INewDwellingPublication,
    IUnitsFilterGroups,
} from "../new-new-dwelling/newdwelling.model";
import { rxResource } from "@angular/core/rxjs-interop";
import { ImagePackage } from "src/models/common/image.model";
import { ResourceService } from "src/services/resources/resource.service";
import { ILatLng } from "@models/map/map.model";
import { ConvertUtil } from "src/util/tools/convert.util";
import { createAndShowExcel } from "src/util/tools/excel.utils";
import { createAndShowPdfFileRaw } from "src/util/tools/pdf.utils";
import { UnitComment } from "src/models/new-dwelling/unit-comment.model";

@Injectable()
export class NewDwellingDataService {
    private readonly http = inject(HttpDataService);
    private readonly httpClient = inject(HttpClient);
    private readonly resourceService = inject(ResourceService);

    private readonly projectId = signal<number>(null);
    private readonly projectStageEstateTypeId = signal<number>(null);


    private readonly effectiveUnitId = computed(() => {
        const stageId = this.projectStageEstateTypeId();
        if (stageId) {
            return Math.floor(stageId / 100);
        }
        return this.projectId();
    });

    unitCommentsResource: ResourceRef<UnitComment[]> = this.getUnitCommentsDataResource();


    public unitsFilterGroups = computed(() => this.projectStageEstateTypeId() ? this.unitsFilterGroupsResource.value() : null);

    public readonly pid = this.projectId.asReadonly();
    public readonly pseid = this.projectStageEstateTypeId.asReadonly();

    public setProjectId(projectId: number): void {
        this.projectId.set(projectId);
    }

    public setProjectStageEstateTypeId(projectStageEstateTypeId: number): void {
        this.projectStageEstateTypeId.set(projectStageEstateTypeId);
    }

    public projectData: Signal<INewDwellingProjectData> = computed(() => {
        const data = {
            projectInfo: this.projectDataResource.hasValue() ? this.projectDataResource.value() : null,
            projectStage: this.projectStageResource.hasValue() ? this.projectStageResource.value() : null,
            prospectUnits: this.projectStageProspectUnitsResource.hasValue() ? this.projectStageProspectUnitsResource.value() : null,
        } as INewDwellingProjectData;

        data.projectInfo?.stageEstateTypesModel?.projectStageEstateTypes.forEach(stage => {
            stage.estateTypeCssString = ConvertUtil.estateTypeToCssString(stage.estateType);
        });
        data.neighbourhood = this.neighbourhoodDataResource.hasValue() ? this.neighbourhoodDataResource.value() : null;
        return data;
    });

    public publications: Signal<INewDwellingPublication[]> = computed(() => {
        const publications = this.projectPublicationsResource.value() || [];

        publications.filter(pub => pub.urlReturnsSuccessResponse);

        const orderByGroup = publications.sort((a, b) => a.groupText.localeCompare(b.groupText));
        orderByGroup.forEach(pub => { pub.groupText = pub.groupText.toLowerCase(); });

        return orderByGroup;
    });

    public imagePackages: Signal<ImagePackage[]> = computed(() => {
        let imagePackages: ImagePackage[] = [];
        if (this.projectStageEstateTypeId()) {
            imagePackages = [...this.stageImages()];
        } else {
            imagePackages = [...this.projectImages()];
        }
        if (this.blomImagesResource?.hasValue()) {
            imagePackages = [...imagePackages, ...this.blomImagesResource.value()];
        }
        return imagePackages;
    });

    public stages = computed(() => this.projectData()?.projectInfo?.stageEstateTypesModel?.projectStageEstateTypes ?? []);

    public position: Signal<ILatLng> = computed((): ILatLng => {
        let latlng: ILatLng = null;
        const projectData = this.projectData();
        if (this.projectStageEstateTypeId()) {
            latlng = { lat: projectData.projectStage?.latitude, lng: projectData?.projectStage?.longitude };
        } else if (this.stages() && this.stages().length > 0) {
            latlng = { lat: this.stages()[0].latitude, lng: this.stages()[0].longitude };
        }
        return latlng;
    });

    getAndOpenNewDwellingUnitsExcel(projectStageEstateTypeId: number, workbookName: string) {
        this.getNewDwellingUnitsExcel(projectStageEstateTypeId, workbookName).subscribe({
            next: (res) => {
                createAndShowExcel(res);
            },
            error: (err) => {
                console.error("Error exporting to Excel:", err);
            },
        });
    }

    getAndOpenNewDwellingProjectFile(filename: string) {
        const url = `${environment.resourceApiUrl}/Resources/NewDwellingProjectsWithToken?fileName=${filename}`;

        this.httpClient.get(url, {
            responseType: 'blob',
            observe: 'response'
        }).subscribe({
            next: (response: HttpResponse<Blob>) => {
                const blob = response.body;
                if (!blob) {
                    console.error('No blob data received');
                    return;
                }

                const blobUrl = window.URL.createObjectURL(blob);
                window.open(blobUrl, '_blank');
            },
            error: (err: any) => {
                console.error('Error opening PDF:', err);
            },
        });
    }

    public getAndOpenDocument(documentInfo: INewDwellingDocumentInfo) {

        documentInfo.isLoading = true;
        this.httpClient.get(documentInfo.url, {
            responseType: 'blob',
            observe: 'response'
        }).subscribe({
            next: (response: HttpResponse<Blob>) => {
                createAndShowPdfFileRaw(response.body)
                documentInfo.isLoading = false;
            },
            error: (err: any) => {
                console.error('Error opening PDF:', err);
                documentInfo.isLoading = false;
            },
        });
    }

    private getNewDwellingUnitsExcel(projectStageEstateTypeId: number, workbookName: string): Observable<string[]> {
        return this.http
            .get<{ data: string[] }>(`${environment.apiUrl}/Report/${projectStageEstateTypeId}/GetNewDwellingUnitsExcel`, { workbookName })
            .pipe(map((response) => response.data));
    }

    private readonly projectStageResource: ResourceRef<INewDwellingInfo | null> = this.http.getResource<INewDwellingInfo>(
        computed(() => this.projectStageEstateTypeId() ? `${environment.apiUrl}/NewDwelling/${this.projectStageEstateTypeId()}` : null),
        [tap((info: INewDwellingInfo) => {
            if (info) {
                this.setDocumentUrls(info.priceList);
                this.setDocumentUrls(info.prospects);
            }
        })]
    );

    private readonly projectStageProspectUnitsResource: ResourceRef<INewDwellingProspectUnits | null> = this.http.getResource<INewDwellingProspectUnits>(
        computed(() => this.projectStageEstateTypeId() ? `${environment.apiUrl}/NewDwelling/${this.projectStageEstateTypeId()}/NewDwellingProspectUnits` : null)
    );

    private readonly projectPublicationsResource = rxResource({
        params: () => {
            const projectId = this.projectId();
            const projectStageEstateTypeId = this.projectStageEstateTypeId();

            if (!!projectStageEstateTypeId) {
                // to get the unit id
                return Math.floor(projectStageEstateTypeId / 100);
            }
            return projectId;
        },
        stream: ({ params: unitId }) => {
            return unitId ? this.getPublications(unitId) : of([]);
        },
    })


    private readonly projectDataResource = rxResource({
        params: () => this.projectId() ?? undefined,
        stream: ({ params: id }) => {
            return this.getProjectInfo(id);
        },
    });

    private readonly neighbourhoodDataResource: ResourceRef<INeighborhoodModel | null> = this.http.getResource<INeighborhoodModel>(
        computed(() => {
            const unitId = this.projectStageResource.value()?.unitIdNear;
            return unitId ? `${environment.apiUrl}/NewDwelling/${unitId}/Neighborhood` : null;
        })
    );

    getUnitCommentsDataResource(): ResourceRef<UnitComment[] | null> {
        const url = computed(() => this.effectiveUnitId() ? `${environment.apiUrl}/NewDwelling/${this.effectiveUnitId()}/UnitComments` : null);
        return this.http.getResource<UnitComment[]>(url);
    }

    private readonly unitsFilterGroupsResource = rxResource({
        params: () => this.projectStageEstateTypeId(),
        stream: ({ params: stageId }) => {
            if (stageId) {
                return this.http.get<{ data: IUnitsFilterGroups }>(`${environment.apiUrl}/NewDwelling/${stageId}/UnitsFilterGroups`).pipe(map(response => response.data));
            }
            return of(null);
        },
    });

    private getProjectInfo(projectId: number): Observable<INewDwellingProjectInfo> {
        return this.http
            .get<{ data: INewDwellingProjectInfo }>(`${environment.apiUrl}/NewDwelling/${projectId}/NewDwellingProjectInfo`)
            .pipe(map((response) => {
                const projectInfo = response.data;
                this.setDocumentUrls(projectInfo.priceList);
                this.setDocumentUrls(projectInfo.prospects);
                return projectInfo;
            }));
    }

    private setDocumentUrls(documents: INewDwellingDocumentInfo[]) {
        if (documents && documents.length > 0) {
            documents.forEach((doc) => {
                doc.url = this.resourceService.getNewDwellingDocumentUrl(doc.newDwellingDocumentId);
            });
        }
    }

    private getPublications(unitId: number): Observable<INewDwellingPublication[]> {
        return this.http.get<{ data: INewDwellingPublication[] }>(`${environment.apiUrl}/NewDwelling/${unitId}/Publications`).pipe(map((response) => response.data));
    }

    private projectImages = computed((): ImagePackage[] => {
        const projectImagePathsArray: ImagePackage[] = [];

        if (this.projectData()?.projectInfo?.pictures?.length > 0) {

            projectImagePathsArray.push(...this.createImagePackagesFromNewDwlellingPictures(null, this.projectData().projectInfo.pictures));
        }

        return projectImagePathsArray;
    });

    private stageImages = computed((): ImagePackage[] => {
        const projectImagePathsArray: ImagePackage[] = [];

        if (this.projectData()?.projectStage?.pictures?.length > 0) {
            projectImagePathsArray.push(...this.createImagePackagesFromNewDwlellingPictures(this.projectData().projectStage.stageName, this.projectData().projectStage.pictures));
        }

        return projectImagePathsArray;
    });


    private createImagePackagesFromNewDwlellingPictures(stageName: string, pictures: INewDwellingDocumentInfo[]): ImagePackage[] {
        const imagePackages: ImagePackage[] = [];

        const imagePackage = new ImagePackage();
        imagePackage.group = stageName?.length > 0 ? stageName : "Prospekt";
        imagePackage.pictureDate = new Date();
        imagePackage.images = pictures.map((pic) => ({
            imageUrl: this.resourceService.getNewDwellingDocumentUrl(pic.newDwellingDocumentId),
            largeThumbnailUrl: this.resourceService.getNewDwellingDocumentUrl(pic.newDwellingDocumentId, true),
            imageId: pic.newDwellingDocumentId
        }));

        imagePackages.push(imagePackage);
        return imagePackages;
    }

    private createImagePackages(paths: INewDwellingProjectStageImages[] | INewDwellingProjectStageImages): ImagePackage[] {
        const imagePackages: ImagePackage[] = [];
        let projectImagePaths: INewDwellingProjectStageImages[] = null;
        if (paths == null) {
            return imagePackages;
        }
        if (paths instanceof Array) {
            projectImagePaths = paths;
        } else {
            projectImagePaths = [paths];
        }
        if (projectImagePaths != null && projectImagePaths.length > 0) {
            projectImagePaths.forEach((p) => {
                const images = p.images.map((imagepath) => ({
                    imageUrl: this.resourceService.getNewDwellingImageUrl(imagepath),
                    largeThumbnailUrl: this.resourceService.getNewDwellingImageUrl(imagepath, true),
                    imageId: imagepath,
                }));

                const imagePackage = new ImagePackage();
                imagePackage.group = p.stageName?.length > 0 ? p.stageName : "Prospekt";
                imagePackage.pictureDate = new Date();
                imagePackage.images = images;
                imagePackages.push(imagePackage);
            });
        }
        return imagePackages;
    }

    private readonly blomImagesResource: ResourceRef<ImagePackage[]> = rxResource({
        params: () => this.position(),
        stream: ({ params: position }) => {
            if (position) {
                return this.resourceService.getBlomImagePackage({ lat: position.lat, lng: position.lng }).pipe(debounceTime(300));
            }
            return of([]);
        },
    });
}
